import moment from 'moment'

import { CarNode } from '@/apollo/graphql'
import {
  carDisplayLabel,
  customerDisplayLabel,
  driverDisplayLabel,
  getMaskedString,
} from '@/utils'
import { t } from 'i18next'

const DATE_FORMAT = 'DD/MM/YYYY'

export const buildData = (selectedItem: Partial<CarNode>, isRTL: boolean) => {
  return {
    general_info: [
      {
        name: 'id',
        label: 'id',
        type: 'text',
        editable: false,
        className: 'w-50',
        value: selectedItem?.id,
      },
      {
        type: 'text',
        name: 'number',
        className: 'w-50',
        label: 'car_number',
        value: getMaskedString(selectedItem?.number, true),
        props: {
          isCarNumber: true,
        },
      },
      {
        type: 'text',
        className: 'w-50',
        name: 'vehicleCode',
        label: 'chassis_number',
        value: selectedItem?.vehicleCode,
      },
      {
        type: 'text',
        className: 'w-50',
        name: 'frameSerialNumber',
        label: 'price_list_number',
        value: selectedItem?.frameSerialNumber,
      },
      {
        label: 'status',
        value: t(selectedItem?.status || ''),
        style: { color: '#36af69' },
      },
      {
        label: 'license_expiration',
        value: moment(selectedItem?.licenceExpDate).format(DATE_FORMAT),
      },
      {
        label: 'insurance_expiration',
        value: moment(selectedItem?.insuranceExpDate).format(DATE_FORMAT),
      },
      {
        label: 'mortgage_bank',
        value: selectedItem?.bank?.name,
      },
      {
        label: 'brand',
        value: selectedItem?.carSubModel?.carModel?.brand?.name,
      },
      {
        label: 'model',
        value: selectedItem?.carSubModel?.carModel?.name,
      },
      {
        label: 'sub_model',
        value: selectedItem?.carSubModel?.name,
      },
      {
        label: 'creation_year',
        value: selectedItem?.creationYear,
      },
      {
        label: 'serviceProvider',
        value: selectedItem?.serviceProvider,
      },
      {
        label: 'driver',
        value: driverDisplayLabel(selectedItem.driver),
        link: selectedItem?.driver?.id
          ? `/${selectedItem?.customer?.id || 'customer'}/drivers/${
              selectedItem?.driver?.id
            }`
          : undefined,
      },
      {
        label: 'deal',
        value: selectedItem.activeDeal?.id,
        link: selectedItem.activeDeal?.id
          ? `/${selectedItem?.customer?.id || 'customer'}/deals/${
              selectedItem.activeDeal?.id
            }`
          : undefined,
      },
      {
        label: 'vehicleCode',
        value: selectedItem?.vehicleCode,
      },
    ],
    driver: [
      {
        type: 'text',
        name: 'name',
        label: 'name',
        required: true,
        className: 'w-50',
        value: selectedItem?.driver?.name,
      },
      {
        type: 'text',
        name: 'email',
        label: 'email',
        required: true,
        className: 'w-50',
        value: selectedItem?.driver?.email,
      },
      {
        type: 'text',
        name: 'address',
        label: 'address',
        className: 'w-50',
        value: selectedItem?.driver?.address,
      },
      {
        type: 'text',
        className: 'w-50',
        name: 'additionalAddress',
        label: 'additionalAddress',
        value: selectedItem?.driver?.additionalAddress,
      },
      {
        type: 'text',
        name: 'city',
        label: 'city',
        className: 'w-50',
        value: selectedItem?.driver?.city,
      },
      {
        name: 'zip',
        label: 'zip',
        type: 'text',
        className: 'w-50',
        value: selectedItem?.driver?.zip,
      },
      {
        type: 'text',
        name: 'phone',
        label: 'phone',
        className: 'w-50',
        value: selectedItem?.driver?.phone,
      },
      {
        type: 'text',
        name: 'country',
        label: 'country',
        className: 'w-50',
        value: selectedItem?.driver?.country,
      },
      {
        label: 'birthday',
        value: selectedItem?.driver?.birthday,
      },
      {
        type: 'text',
        required: true,
        name: 'idNumber',
        className: 'w-50',
        label: 'id_number',
        value: selectedItem?.driver?.idNumber,
      },
      {
        label: 'customer',
        value: customerDisplayLabel(selectedItem?.driver?.customer),
      },
    ],
    customer: [
      {
        type: 'text',
        name: 'name',
        label: 'name',
        required: true,
        className: 'w-50',
        value: customerDisplayLabel(selectedItem?.driver?.customer),
      },
      {
        type: 'text',
        name: 'email',
        label: 'email',
        className: 'w-50',
        value: selectedItem?.driver?.customer?.email,
      },
      {
        type: 'text',
        name: 'title',
        label: 'title',
        className: 'w-50',
        value: selectedItem?.driver?.customer?.title,
      },
      {
        type: 'text',
        name: 'phone',
        label: 'phone',
        className: 'w-50',
        value: selectedItem?.driver?.customer?.phone,
      },
      {
        type: 'text',
        className: 'w-50',
        name: 'additional_phone',
        label: 'additional_phone',
        value: selectedItem?.driver?.customer?.additionalPhone,
      },
      {
        name: 'fax',
        type: 'text',
        label: 'fax',
        className: 'w-50',
        value: selectedItem?.driver?.customer?.fax,
      },
      {
        type: 'text',
        className: 'w-50',
        name: 'additional_fax',
        label: 'additional_fax',
        value: selectedItem?.driver?.customer?.additionalFax,
      },
      {
        type: 'text',
        name: 'address',
        label: 'address',
        className: 'w-50',
        value: selectedItem?.driver?.customer?.address,
      },
      {
        type: 'text',
        className: 'w-50',
        name: 'additional_address',
        label: 'additional_address',
        value: selectedItem?.driver?.customer?.additionalAddress,
      },
      {
        type: 'text',
        name: 'city',
        label: 'city',
        className: 'w-50',
        value: selectedItem?.driver?.customer?.city,
      },
      {
        type: 'text',
        name: 'country',
        label: 'country',
        className: 'w-50',
        value: selectedItem?.driver?.customer?.country,
      },
      {
        name: 'zip',
        label: 'zip',
        type: 'text',
        className: 'w-50',
        value: selectedItem?.driver?.customer?.zip,
      },
      {
        type: 'text',
        className: 'w-50',
        name: 'bank_account_number',
        label: 'bank_account_number',
        value: selectedItem?.driver?.customer?.bankAccountNumber,
      },
      {
        type: 'text',
        className: 'w-50',
        name: 'branch_number',
        label: 'branch_number',
        value: selectedItem?.driver?.customer?.branchNumber,
      },
      {
        type: 'text',
        name: 'type',
        label: 'type',
        required: true,
        className: 'w-50',
        value: selectedItem?.driver?.customer?.type,
      },
      {
        type: 'text',
        className: 'w-50',
        name: 'company_name',
        label: 'company_name',
        value: selectedItem?.driver?.customer?.contactName,
      },
      {
        type: 'text',
        className: 'w-50',
        name: 'company_number',
        label: 'company_number',
        value: selectedItem?.driver?.customer?.companyNumber,
      },
    ],
  }
}
